import { useQuery } from "react-query";
import { useCurrentUser } from "../functional-components/UserContext";
import apiService from "../services/apiService";
import { getProductAsync, getProductByCockpitId } from "../utils/ProductUtils";
import { useLang } from "./LangHooks";
import { useLocSettings, useSettings } from "./SettingsHooks";
import { cmsAssetUrl } from "../utils/CmsUtils";
import { enhanceProductSpecifications } from "../utils/SpecsUtils";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import { Product } from "../types/Product";
import { ProductCategory } from "../types/ProductCategory";
import { Language } from "../config/languages";
import { useProductCategories } from "./ProductCategoryHooks";
import { useSpecsTranslation } from "./TranslationHooks";
import { useSiteFeatures } from "./useSiteFeatures";
import { useEffect, useState } from "react";

/** Returns public products with public prices */
export function usePublicProducts(): Product[] {
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const lang = useLang();
  const { data: products } = useQuery<Product[]>(
    ["products", lang],
    async () => {
      return await apiService.loadProducts(lang, features, settings);
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

/** Returns user visible products with user specific prices */
export function useUserProducts(): Product[] {
  const settings = useLocSettings();
  const features = useSiteFeatures();
  const { userSecrets } = useCurrentUser();
  const lang = useLang();
  const queryKey = userSecrets
    ? ["userproducts", lang, userSecrets]
    : ["products", lang];
  const { data: products } = useQuery<Product[]>(
    queryKey,
    async () => {
      return await apiService.loadProducts(lang, features, settings);
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

/** Returns public product with public prices */
export function usePublicProductByLang(
  artId: string,
  lang: Language
): Product | null {
  const settings = useSettings();
  const products = usePublicProducts();

  const { data: product } = useQuery(
    ["product", lang, artId],
    async (): Promise<Product | null> => {
      const product: Product | null = (await getProductAsync(
        products,
        artId,
        lang,
        false
      )) as Product;
      return enhanceProductSpecifications(product, settings);
    },
    { refetchInterval: 1000 * 60 * 15 }
  );

  //console.log("usePublicProduct", product);
  return product || null;
}

/** Returns public product with public prices */
export function usePublicProduct(artId: string): Product | null {
  const lang = useLang();
  return usePublicProductByLang(artId, lang);
}

/** Returns product with user specific prices */
export function useUserProduct(artId: string): Product | null {
  const settings = useSettings();
  const products = useUserProducts();
  const { userSecrets } = useCurrentUser();
  const lang = useLang();

  const withAuth = !!userSecrets;
  const queryKey = withAuth
    ? ["userproduct", lang, artId, userSecrets]
    : ["product", lang, artId];
  const { data: product } = useQuery(
    queryKey,
    async (): Promise<Product | null> => {
      const product = (await getProductAsync(
        products,
        artId,
        lang,
        withAuth
      )) as Product;
      return enhanceProductSpecifications(product, settings);
    },
    { refetchInterval: 1000 * 60 * 15 }
  );

  //console.log("useUserProduct", product);
  return product || null;
}

export function useFittingProduct(artId: string): Product | null {
  const userProduct = useUserProduct(artId);
  const publicProduct = usePublicProduct(artId);
  const [product, setProduct] = useState(userProduct);
  useEffect(() => {
    if (userProduct?.sku) {
      setProduct({ ...userProduct });
    } else {
      setProduct({
        ...publicProduct,
        price: undefined,
        stock: undefined
      } as Product);
    }
  }, [userProduct, publicProduct]);
  return product;
}

export function useProductWithCockpitId(id: string): Product | undefined {
  const products = usePublicProducts();
  return getProductByCockpitId(products, id);
}

export function usePublicProductsByProductType(productType: string): Product[] {
  const settings = useSettings();
  const lang = useLang();

  const { data: products } = useQuery(
    ["productsByProductType", lang, productType],
    async (): Promise<Product[]> => {
      const products = productType
        ? ((await apiService.getPublicProductsByProductType(
            productType,
            lang
          )) as Product[])
        : [];
      return products.map(product => {
        return enhanceProductSpecifications(product, settings);
      });
    },
    { refetchInterval: 1000 * 60 * 15 }
  );
  return products || [];
}

export function useProductLabels(product?: Product) {
  const settings = useLocSettings();
  const ts = useSpecsTranslation();
  if (!product) {
    return [];
  }
  console.log("labels", settings.labels);
  return Object.keys(settings.labels)
    .map(key => {
      const labels = settings.labels[key];
      if (!Array.isArray(labels)) {
        return null;
      }
      return labels.map(label => {
        if (product.specsIndexed?.[key]?.value === label.name) {
          const labelName = ts(label.key, label.name);
          const imagePath = cmsAssetUrl(label.logo?.path);
          return { ...label, imagePath, name: labelName };
        }
        return null;
      });
    })
    .flat()
    .filter(o => o !== null);
}

/**
 * Returns all product categories without a parent
 */
export function useRootCategories(): ProductCategory[] {
  const categories = useProductCategories();
  //console.log("categories", categories);
  return cloneDeep(categories).filter(pc => !pc.parent);
}

export function useCategory(_id?: string): ProductCategory | undefined {
  const categories = useProductCategories();
  return _id ? find(categories, { _id }) : undefined;
}
